import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CoolIcon } from "@/components/ui/cool-icon";
import { Loader2 } from "@/components/ui/icons";
import { extractApiErrorMessage } from "@/lib/api";
import { assignAccountBot, listBots, type BotRow } from "@/lib/api/bots";
import { cn } from "@/lib/utils";

interface ReassignBotDialogProps {
  accountSlug: string;
  currentBotId: string | null;
  onDone: () => Promise<void> | void;
}

function fmtSeen(ts: number | null): string {
  if (!ts) return "never seen";
  const diff = Date.now() - ts;
  if (diff < 60_000) return "seen just now";
  if (diff < 3_600_000) return `seen ${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `seen ${Math.floor(diff / 3_600_000)}h ago`;
  return `seen ${Math.floor(diff / 86_400_000)}d ago`;
}

function isLive(lastSeen: number | null): boolean {
  if (!lastSeen) return false;
  return Date.now() - lastSeen < 120_000;
}

export function ReassignBotDialog({
  accountSlug,
  currentBotId,
  onDone,
}: ReassignBotDialogProps) {
  const [open, setOpen] = useState(false);
  const [bots, setBots] = useState<BotRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currentBot = bots?.find((b) => b.id === currentBotId) ?? null;
  const triggerLabel = currentBot?.hostname ?? currentBot?.id ?? "Unassigned";
  const action = currentBotId ? "Reassign Bot" : "Assign Bot";

  // Fetch bot list when the dialog opens. One-shot — the operator
  // closes and reopens to refresh, matching the reversible-action shape.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBots(null);
    setLoadError(null);
    setSelectedId(null);
    void (async () => {
      try {
        const res = await listBots();
        if (cancelled) return;
        setBots(res.filter((b) => b.status === "active"));
      } catch (e) {
        if (cancelled) return;
        setLoadError(extractApiErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleConfirm = async (): Promise<void> => {
    if (!selectedId || selectedId === currentBotId) return;
    setSubmitting(true);
    try {
      await assignAccountBot(accountSlug, selectedId);
      const target = bots?.find((b) => b.id === selectedId);
      const label = target?.hostname ?? target?.id ?? selectedId;
      toast.success(
        currentBotId
          ? `Reassigned to ${label} — old bot draining, new bot spinning up`
          : `Assigned to ${label} — bot spinning up`,
      );
      setOpen(false);
      await onDone();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="gap-1.5"
      >
        <CoolIcon name="Arrow_Left_Right" />
        <span>{action}</span>
        <span className="text-muted-foreground">·</span>
        <span className="font-mono text-[11px]">{triggerLabel}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{action}</DialogTitle>
            <DialogDescription>
              Move this account to a different bot host. The current bot will
              drain its worker and close its browser; the new bot will spawn a
              fresh worker. The new host creates a fresh browser profile on
              first login, so the firm sees a new device the next time the bot
              connects.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            {bots == null && !loadError && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading bots…
              </div>
            )}

            {loadError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                Couldn&rsquo;t load bots: {loadError}
              </div>
            )}

            {bots != null && bots.length === 0 && (
              <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                No active bots enrolled.{" "}
                <Link
                  to="/bots"
                  className="font-medium text-foreground underline underline-offset-2"
                  onClick={() => setOpen(false)}
                >
                  Provision one from the Bots page.
                </Link>
              </div>
            )}

            {bots != null && bots.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {bots.map((bot, i) => {
                  const isCurrent = bot.id === currentBotId;
                  const isSelected = bot.id === selectedId;
                  const live = isLive(bot.last_seen);
                  return (
                    <motion.li
                      key={bot.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03, duration: 0.18 }}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedId(bot.id)}
                        disabled={isCurrent}
                        className={cn(
                          "flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors",
                          isCurrent &&
                            "cursor-not-allowed border-border/40 bg-muted/30 opacity-70",
                          !isCurrent &&
                            isSelected &&
                            "border-accent bg-accent/10",
                          !isCurrent &&
                            !isSelected &&
                            "border-border/60 hover:border-border hover:bg-muted/30",
                        )}
                      >
                        <div className="flex min-w-0 flex-col">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-mono text-xs font-medium text-foreground">
                              {bot.hostname ?? bot.id}
                            </span>
                            {isCurrent && (
                              <span className="rounded-full border border-border/60 bg-muted/50 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                Current
                              </span>
                            )}
                            <span
                              className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                live ? "bg-emerald-500" : "bg-muted-foreground/40",
                              )}
                              aria-label={live ? "live" : "stale"}
                            />
                          </div>
                          <span className="truncate text-[11px] text-muted-foreground">
                            {bot.assigned_account_count}{" "}
                            {bot.assigned_account_count === 1
                              ? "account"
                              : "accounts"}{" "}
                            · {fmtSeen(bot.last_seen)}
                            {bot.version ? ` · v${bot.version}` : ""}
                          </span>
                        </div>
                        {!isCurrent && isSelected && (
                          <CoolIcon
                            name="Check"
                            className="text-accent"
                            label="selected"
                          />
                        )}
                      </button>
                    </motion.li>
                  );
                })}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleConfirm()}
              disabled={
                submitting ||
                selectedId == null ||
                selectedId === currentBotId
              }
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  {currentBotId ? "Reassigning…" : "Assigning…"}
                </>
              ) : (
                action
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
